import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { UseQueryResult } from "@tanstack/react-query";
import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  ApiOrganizationEhrIntegrationTypeChoices,
  ApiOrganizationOrganizationTypeChoices,
  GetLoginProviderQuery,
  GetUserOrgnizationDetailsQuery,
  useGetLoginProviderQuery,
  useGetUserAccountDetailsQuery,
  useGetUserOrgnizationDetailsQuery,
} from "@api/graphql/types-and-hooks";
import { setRestAuthGetter } from "@api/rest/rest-service";
import { useAuthorization } from "@api/useAuthorization";
import { convertReadableString } from "@components/forms/utils";

import centralReachLogo from "../assets/ehr/central-reach.svg";
import ensoraLogo from "../assets/ehr/ensora.png";
import hippHealthLogo from "../assets/ehr/hipp-health.png";
import hirasmusLogo from "../assets/ehr/hirasmus.svg";
import logoPlaceholder from "../assets/ehr/logo-placeholder.png";
import motivityLogo from "../assets/ehr/motivity.svg";
import passageLogo from "../assets/ehr/passage.png";
import rethinkLogo from "../assets/ehr/rethink.png";
import therapzLogo from "../assets/ehr/therapz.png";
import fronteraLogo from "../assets/icon.png";

const organizationLogoMap: Partial<Record<ApiOrganizationOrganizationTypeChoices, string>> = {
  [ApiOrganizationOrganizationTypeChoices.Ensora]: ensoraLogo,
  [ApiOrganizationOrganizationTypeChoices.FronteraHealth]: fronteraLogo,
  [ApiOrganizationOrganizationTypeChoices.FronteraEhr]: fronteraLogo,
  [ApiOrganizationOrganizationTypeChoices.HiRasmus]: hirasmusLogo,
  [ApiOrganizationOrganizationTypeChoices.Measurepm]: logoPlaceholder,
  [ApiOrganizationOrganizationTypeChoices.Motivity]: motivityLogo,
  [ApiOrganizationOrganizationTypeChoices.PassageHealth]: passageLogo,
  [ApiOrganizationOrganizationTypeChoices.Therapz]: therapzLogo,
  [ApiOrganizationOrganizationTypeChoices.CentralReach]: centralReachLogo,
  [ApiOrganizationOrganizationTypeChoices.HippHealth]: hippHealthLogo,
  [ApiOrganizationOrganizationTypeChoices.Rethink]: rethinkLogo,
} as const;

interface AdminDataContextI {
  orgnizationDetailsQuery: UseQueryResult<GetUserOrgnizationDetailsQuery, unknown>;
  loginProviderDetailsQuery: UseQueryResult<GetLoginProviderQuery, unknown>;
  roles: string[];
  isSelfServeUser: boolean;
  isPassageHealthUser: boolean;
  isTherapzUser: boolean;
  isHippHealthUser: boolean;
  assessmentCreditsRemaining: number;
  assessmentCreditsUsed: number;
  isFetchingAssessmentCredits: boolean;
  isManualEHRSyncEnabled: boolean;
  isOnboardingEnabled: boolean;
  isEhrSyncCleared: boolean;
  isOrgPeerReviewEnabled: boolean;
  /** The organization details request has finished (loaded or failed), so org settings like isOrgPeerReviewEnabled are final. */
  isOrgDetailsLoaded: boolean;
  isConsentRequired: boolean;
  isStepAheadUser: boolean;
  isCatalightUser: boolean;
  isButterflyEffectsUser: boolean;
  isAcesUser: boolean;
  isAT4KUser: boolean;
  isCentriaUser: boolean;
  isLearnUser: boolean;
  isKuwaitUser: boolean;
  isTestAcesUser: boolean;
  isABCUser: boolean;
  isUserCaregiver: boolean;
  isUserRBT: boolean;
  isUserBCBA: boolean;
  isUserEnterpriseAdmin: boolean;
  isFronteraHealthTypeOrg: boolean;
  isHiRasmusUser: boolean;
  isMotivityUser: boolean;
  isCentralReachUser: boolean;
  isRethinkUser: boolean;
  isEnsoraUser: boolean;
  isFronteraEhrUser: boolean;
  orgId: string | undefined;
  orgName: string | undefined;
  externalOrganizationName: string;
  organizationLogo: string;
  isEHROrgUser: boolean;
  isFronteraClarityOrg: boolean;
  isDataWarehouseOrg: boolean;
  isOrgUsernamePasswordOrg: boolean;
  organizationType: ApiOrganizationOrganizationTypeChoices;
}

const AdminContext = createContext<AdminDataContextI | undefined>(undefined);

export const useAdminData = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error("useAdminData must be used within a AdminDataProvider");
  }
  return context;
};

export const AdminDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [isSelfServeUser, setIsSelfServeUser] = useState<boolean>(false);

  const { user, isAuthenticated } = useAuth0();
  const { getAuthorizationHeader } = useAuthorization();

  useEffect(() => {
    setRestAuthGetter(getAuthorizationHeader);
  }, [getAuthorizationHeader]);

  useEffect(() => {
    if (user) {
      try {
        const rolesKey = `${config.auth0Audience}/roles`;
        const rolesString = user?.[rolesKey];

        const selfServeKey = `${config.auth0Audience}/selfServeUser`;
        const selfServeString = user?.[selfServeKey];

        const isSelfServe =
          !!selfServeString && typeof selfServeString === "string" && selfServeString.toLowerCase() === "true";

        setIsSelfServeUser(isSelfServe);
        if (rolesString.length) setRoles(rolesString.split(","));
      } catch (err) {
        console.error("Error getting roles ", err);
        setRoles([]);
      }
    }
  }, [user]);

  const accountDetails = useGetUserAccountDetailsQuery(
    {},
    {
      queryKey: ["get-account-details", isSelfServeUser],
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
      staleTime: Infinity,
      enabled: isSelfServeUser,
    },
  );

  const assessmentCreditsRemaining = accountDetails?.data?.getUserAccountDetails?.pendingAssessment || 0;
  const totalCredits = accountDetails?.data?.getUserAccountDetails?.purchasedAssessment || 0;
  const assessmentCreditsUsed = totalCredits - assessmentCreditsRemaining;
  const isUserCaregiver: boolean = roles.includes("caregiver") && !roles.includes("bcba") && !roles.includes("admin");
  const isUserRBT: boolean = roles.includes("rbt") && !roles.includes("bcba") && !roles.includes("admin");
  const isUserBCBA: boolean = roles.includes("bcba") && !roles.includes("admin");
  const isUserEnterpriseAdmin: boolean = roles.includes("enterprise-admin");

  const loginProviderDetailsQuery = useGetLoginProviderQuery(
    {},
    {
      queryKey: ["get-login-provider-details", JSON.stringify(roles), isAuthenticated, roles],
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
      staleTime: Infinity,
      enabled: isAuthenticated && !!roles.length && !isUserCaregiver,
    },
  );

  const orgnizationDetailsQuery = useGetUserOrgnizationDetailsQuery(
    {},
    {
      queryKey: ["get-account-details", isAuthenticated, JSON.stringify(roles), roles],
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
      staleTime: Infinity,
      enabled: isAuthenticated && !!roles.length && !isUserCaregiver,
    },
  );

  const orgDetails = orgnizationDetailsQuery.data?.getUserOrgnizationDetails;
  const orgType = orgDetails?.organizationType;
  const orgName = orgDetails?.name;
  // Frontera-internal staff run EHR setup/validation during the managed-
  // onboarding window, so the gate never applies to them — treat internal as
  // cleared for all EHR UI. The backend mirrors this (frontera-internal
  // passes enforce_ehr_sync_gate), so the enabled controls actually work.
  const isEhrSyncCleared = Boolean(orgDetails?.ehrSyncCleared) || roles.includes("frontera-internal");
  const isOrgPeerReviewEnabled = Boolean(orgDetails?.peerReviewEnabled);
  const isOrgDetailsLoaded = orgnizationDetailsQuery.isSuccess || orgnizationDetailsQuery.isError || isUserCaregiver;

  const contextValue: AdminDataContextI = useMemo(
    () => ({
      orgnizationDetailsQuery,
      loginProviderDetailsQuery,
      roles,
      isSelfServeUser,
      isPassageHealthUser: orgType === ApiOrganizationOrganizationTypeChoices.PassageHealth,
      isTherapzUser: orgType === ApiOrganizationOrganizationTypeChoices.Therapz,
      isHippHealthUser: orgType === ApiOrganizationOrganizationTypeChoices.HippHealth,
      assessmentCreditsRemaining,
      assessmentCreditsUsed,
      isFetchingAssessmentCredits: accountDetails.isFetching,
      isManualEHRSyncEnabled: Boolean(orgDetails?.manualEhrIntegration),
      isOnboardingEnabled: Boolean(orgDetails?.onboardingEnabled),
      isEhrSyncCleared,
      isOrgPeerReviewEnabled,
      isOrgDetailsLoaded,
      isConsentRequired: Boolean(orgDetails?.consentRequired),
      isStepAheadUser: orgName === "Step Ahead",
      isCentriaUser: orgName === "Centria Healthcare LLC",
      isFronteraClarityOrg: orgName === "Frontera Clarity",
      isCatalightUser: orgName === "Catalight" || orgName === "ACES",
      // Prefix match: the org name varies by environment (e.g. "Butterfly Effects Dev Test" on dev).
      isButterflyEffectsUser: Boolean(orgName?.startsWith("Butterfly Effects")),
      isAcesUser: orgName === "ACES",
      isAT4KUser: orgName === "AT4K",
      isLearnUser: orgName === "LEARN Behavioral",
      isKuwaitUser: orgName === "ABC Kuwait",
      isABCUser: orgName === "Action Behavior Centers",
      isTestAcesUser: orgName === "TEST ACES",
      isUserCaregiver,
      isUserRBT,
      isUserBCBA,
      isUserEnterpriseAdmin,
      isHiRasmusUser: orgType === ApiOrganizationOrganizationTypeChoices.HiRasmus,
      isMotivityUser: orgType === ApiOrganizationOrganizationTypeChoices.Motivity,
      isCentralReachUser: orgType === ApiOrganizationOrganizationTypeChoices.CentralReach,
      isRethinkUser: orgType === ApiOrganizationOrganizationTypeChoices.Rethink,
      isEnsoraUser: orgType === ApiOrganizationOrganizationTypeChoices.Ensora,
      isFronteraEhrUser: orgType === ApiOrganizationOrganizationTypeChoices.FronteraEhr,
      isFronteraHealthTypeOrg: orgType === ApiOrganizationOrganizationTypeChoices.FronteraHealth,
      orgId: orgDetails?.id,
      orgName: orgDetails?.name,
      externalOrganizationName: convertReadableString(orgType) || "",
      organizationLogo: (orgType && organizationLogoMap[orgType]) || logoPlaceholder,
      isEHROrgUser:
        orgType !== ApiOrganizationOrganizationTypeChoices.FronteraHealth &&
        orgType !== ApiOrganizationOrganizationTypeChoices.FronteraClarity,
      isDataWarehouseOrg: orgDetails?.ehrIntegrationType === ApiOrganizationEhrIntegrationTypeChoices.DataWarehouse,
      isOrgUsernamePasswordOrg:
        orgDetails?.ehrIntegrationType === ApiOrganizationEhrIntegrationTypeChoices.OrgUsernamePassword,
      organizationType: orgType as ApiOrganizationOrganizationTypeChoices,
    }),
    [
      orgnizationDetailsQuery,
      loginProviderDetailsQuery,
      roles,
      isSelfServeUser,
      orgType,
      orgName,
      orgDetails,
      assessmentCreditsRemaining,
      assessmentCreditsUsed,
      accountDetails.isFetching,
      isUserCaregiver,
      isUserRBT,
      isUserBCBA,
      isUserEnterpriseAdmin,
      isEhrSyncCleared,
      isOrgPeerReviewEnabled,
      isOrgDetailsLoaded,
    ],
  );

  if (!orgnizationDetailsQuery) return null;

  return <AdminContext.Provider value={contextValue}>{children}</AdminContext.Provider>;
};
